// src/components/auth/ProfileModal.tsx
// User profile editor. Shows: avatar, basic info form, subscription status,
// logout, and account-deletion trigger. Opens when the navbar username is clicked.

import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthStore } from '../../stores/useAuthStore';
import { handleProfileUpdate, handleLogout, getAuthErrorMessage } from '../../services/authService';
import { AvatarUpload } from './AvatarUpload';

const C = {
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
  accent:  '#4f8cf7',
  success: '#34d399',
  error:   '#f87171',
};

function planLabel(plan: string | null): string {
  const map: Record<string, string> = {
    monthly: 'Monthly', three_months: '3-Month', yearly: 'Yearly',
  };
  return plan ? (map[plan] ?? plan) : 'None';
}

function subStatusColor(status: string): string {
  if (status === 'active')  return C.success;
  if (status === 'expired') return C.error;
  return C.muted;
}

function msgStyle(type: string): React.CSSProperties {
  const map: Record<string, [string, string, string]> = {
    error:   ['rgba(248,113,113,0.08)',  'rgba(248,113,113,0.22)',  C.error],
    success: ['rgba(52,211,153,0.08)',   'rgba(52,211,153,0.22)',   C.success],
    info:    ['rgba(79,140,247,0.08)',   'rgba(79,140,247,0.22)',   C.accent],
  };
  const [bg, border, color] = map[type] ?? map.info;
  return { padding: '10px 14px', borderRadius: '10px', fontSize: '0.84rem', background: bg, border: `1px solid ${border}`, color };
}

function subBadgeStyle(status: string): React.CSSProperties {
  const map: Record<string, [string, string, string]> = {
    active:   ['rgba(52,211,153,0.1)',  'rgba(52,211,153,0.25)',  C.success],
    expired:  ['rgba(248,113,113,0.1)', 'rgba(248,113,113,0.25)', C.error],
    inactive: ['rgba(139,156,196,0.1)', 'rgba(139,156,196,0.2)',  C.muted],
  };
  const [bg, border, color] = map[status] ?? map.inactive;
  return {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '4px 12px', borderRadius: '999px',
    fontSize: '0.8rem', fontWeight: 700, background: bg, border: `1px solid ${border}`, color,
  };
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'textarea:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

interface FormValues { displayName: string; phone: string; occupation: string; institution: string; }
interface MessageState { text: string; type: 'error' | 'success' | 'info'; }

const INPUT_BASE: React.CSSProperties = {
  width: '100%', padding: '10px 13px',
  background: 'rgba(255,255,255,0.04)',
  border: '1.5px solid rgba(255,255,255,0.08)',
  borderRadius: '10px', color: C.text,
  fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  fontFamily: 'inherit',
};
const LABEL: React.CSSProperties = {
  fontSize: '0.7rem', color: C.muted, marginBottom: '6px',
  display: 'block', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
};
const SECTION_TITLE: React.CSSProperties = {
  fontSize: '0.68rem', fontWeight: 700, color: C.muted,
  textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0,
};

export function ProfileModal(): React.ReactElement | null {
  const open       = useAuthStore((s) => s.profileModalOpen);
  const closeModal = useAuthStore((s) => s.closeProfileModal);
  const openDelete = useAuthStore((s) => s.openDeleteModal);
  const user       = useAuthStore((s) => s.user);
  const profile    = useAuthStore((s) => s.profile);

  const modalRef = useRef<HTMLDivElement>(null);

  const [message, setMessage]               = useState<MessageState | null>(null);
  const [isSaving, setIsSaving]             = useState(false);
  const [isLoggingOut, setIsLoggingOut]     = useState(false);
  const [avatarFile, setAvatarFile]         = useState<File | null>(null);
  const [avatarRemoved, setAvatarRemoved]   = useState(false);
  const [nameFocus,  setNameFocus]  = useState(false);
  const [phoneFocus, setPhoneFocus] = useState(false);
  const [occFocus,   setOccFocus]   = useState(false);
  const [instFocus,  setInstFocus]  = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>();

  useEffect(() => {
    if (open && profile) {
      reset({ displayName: profile.displayName, phone: profile.phone, occupation: profile.occupation, institution: profile.institution });
      setAvatarFile(null); setAvatarRemoved(false); setMessage(null);
    }
  }, [open, profile, reset]);

  useEffect(() => {
    if (!open || !modalRef.current) return;
    document.body.style.overflow = 'hidden';
    const modal = modalRef.current;
    const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    focusable[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab') return;
      const els = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!els.length) { e.preventDefault(); return; }
      const [first, last] = [els[0], els[els.length - 1]];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    modal.addEventListener('keydown', onKey);
    return () => { modal.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, closeModal]);

  const focusGlow = (active: boolean): React.CSSProperties => active
    ? { borderColor: C.accent, boxShadow: '0 0 0 3px rgba(79,140,247,0.1)' }
    : {};

  const onSubmit = async (values: FormValues) => {
    setIsSaving(true); setMessage(null);
    try {
      await handleProfileUpdate({ displayName: values.displayName, phone: values.phone, institution: values.institution, occupation: values.occupation, avatarFile, avatarRemoved });
      setAvatarFile(null); setAvatarRemoved(false);
      setMessage({ text: 'Profile updated successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: getAuthErrorMessage(err), type: 'error' });
    } finally { setIsSaving(false); }
  };

  const onLogout = async () => {
    setIsLoggingOut(true);
    try {
      closeModal(); await handleLogout();
    } catch (err) {
      setIsLoggingOut(false);
      setMessage({ text: err instanceof Error ? err.message : 'Logout failed.', type: 'error' });
    }
  };

  if (!open || !user) return null;

  const displayInitial  = (user.displayName || user.email || '?').charAt(0).toUpperCase();
  const currentAvatarUrl = user.photoURL;
  const sub = profile?.subscription;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      role="dialog" aria-modal="true" aria-label="Profile settings"
    >
      <div ref={modalRef} style={{
        background: '#0d1220',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '20px',
        width: '100%', maxWidth: '500px', maxHeight: '92vh',
        overflowY: 'auto', position: 'relative',
        boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          position: 'sticky', top: 0, background: '#0d1220', zIndex: 1,
        }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: C.text, margin: 0 }}>My Profile</h2>
          <button
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px', cursor: 'pointer', color: C.muted,
              width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={closeModal} aria-label="Close profile"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
            </svg>
          </button>
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {message && <div style={msgStyle(message.type)} role="alert">{message.text}</div>}

          {/* Avatar section */}
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px',
          }}>
            <p style={SECTION_TITLE}>Profile Photo</p>
            <AvatarUpload
              currentUrl={currentAvatarUrl}
              initial={displayInitial}
              hasAvatar={!!currentAvatarUrl || !!avatarFile}
              onFileSelect={(file) => { setAvatarFile(file); setAvatarRemoved(false); }}
              onRemove={() => { setAvatarFile(null); setAvatarRemoved(true); }}
            />
          </div>

          {/* Personal info form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div style={{
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px',
            }}>
              <p style={SECTION_TITLE}>Personal Info</p>

              {/* Read-only email */}
              <div>
                <label style={LABEL}>Email address</label>
                <input type="text" style={{ ...INPUT_BASE, color: C.muted, cursor: 'default' }} value={user.email ?? ''} readOnly tabIndex={-1} />
              </div>

              {/* Display name */}
              <div>
                <label style={LABEL} htmlFor="profileName">Display name</label>
                <input
                  id="profileName" type="text"
                  style={{ ...INPUT_BASE, borderColor: errors.displayName ? C.error : 'rgba(255,255,255,0.08)', ...focusGlow(nameFocus) }}
                  onFocus={() => setNameFocus(true)}
                  {...register('displayName', { required: 'Name is required', onBlur: () => setNameFocus(false) })}
                />
                {errors.displayName && <p style={{ fontSize: '0.75rem', color: C.error, marginTop: '4px' }}>{errors.displayName.message}</p>}
              </div>

              {/* Phone */}
              <div>
                <label style={LABEL} htmlFor="profilePhone">Phone (optional)</label>
                <input
                  id="profilePhone" type="tel"
                  style={{ ...INPUT_BASE, ...focusGlow(phoneFocus) }}
                  onFocus={() => setPhoneFocus(true)}
                  {...register('phone', { onBlur: () => setPhoneFocus(false) })}
                />
              </div>

              {/* Occupation + Institution */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={LABEL} htmlFor="profileOccupation">Role / Occupation</label>
                  <input
                    id="profileOccupation" type="text"
                    style={{ ...INPUT_BASE, ...focusGlow(occFocus) }}
                    onFocus={() => setOccFocus(true)}
                    {...register('occupation', { onBlur: () => setOccFocus(false) })}
                  />
                </div>
                <div>
                  <label style={LABEL} htmlFor="profileInstitution">Club / Institution</label>
                  <input
                    id="profileInstitution" type="text"
                    style={{ ...INPUT_BASE, ...focusGlow(instFocus) }}
                    onFocus={() => setInstFocus(true)}
                    {...register('institution', { onBlur: () => setInstFocus(false) })}
                  />
                </div>
              </div>

              <button
                type="submit" disabled={isSaving}
                style={{
                  width: '100%', padding: '12px', borderRadius: '999px',
                  background: 'linear-gradient(135deg, #4f8cf7 0%, #6366f1 100%)',
                  border: 'none', color: '#fff', fontSize: '0.92rem',
                  fontWeight: 700, cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1,
                  transition: 'opacity 0.15s', fontFamily: 'inherit',
                }}
              >
                {isSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>

          {/* Subscription */}
          {sub && (
            <div style={{
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px',
            }}>
              <p style={SECTION_TITLE}>Subscription</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.84rem', color: C.muted }}>Status</span>
                <span style={subBadgeStyle(sub.status)}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: subStatusColor(sub.status), display: 'inline-block' }} />
                  {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                  {profile?.hasFullAccess ? ' (Full Access)' : ''}
                </span>
              </div>
              {sub.plan && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.84rem', color: C.muted }}>Plan</span>
                  <span style={{ fontSize: '0.84rem', color: C.text, fontWeight: 600 }}>{planLabel(sub.plan)}</span>
                </div>
              )}
              {sub.endDate && !sub.isLifetime && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.84rem', color: C.muted }}>
                    {sub.status === 'active' ? 'Renews' : 'Expired'}
                  </span>
                  <span style={{ fontSize: '0.84rem', color: C.text, fontWeight: 600 }}>
                    {sub.endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
              )}
              {sub.isLifetime && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.84rem', color: C.muted }}>Access</span>
                  <span style={{ fontSize: '0.84rem', color: C.success, fontWeight: 600 }}>Lifetime</span>
                </div>
              )}
              {sub.status === 'inactive' && (
                <p style={{ fontSize: '0.82rem', color: C.muted, margin: 0 }}>
                  You're on the free plan — only Unit 1 is available.
                </p>
              )}
            </div>
          )}

          {/* Account actions */}
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <p style={SECTION_TITLE}>Account</p>
            <button
              type="button"
              style={{
                width: '100%', padding: '11px', borderRadius: '999px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.08)',
                color: C.muted, fontSize: '0.88rem', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
                opacity: isLoggingOut ? 0.7 : 1, transition: 'opacity 0.15s',
              }}
              disabled={isLoggingOut} onClick={onLogout}
            >
              {isLoggingOut ? 'Signing out…' : 'Sign Out'}
            </button>
            <button
              type="button"
              style={{
                width: '100%', padding: '11px', borderRadius: '999px',
                background: 'transparent',
                border: '1px solid rgba(248,113,113,0.3)',
                color: C.error, fontSize: '0.88rem', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
              onClick={() => { closeModal(); openDelete(); }}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
